import { Link } from 'react-router-dom';
import { ExternalLink, Calendar, Users, FileText, Layers } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';

export interface CampaignWithCounts {
  id: string;
  name: string;
  description: string | null;
  status: string;
  created_at: string;
  survey_campaign_surveys: { count: number }[];
  survey_invitations: { respondent_id: string }[];
}

interface CampaignListProps {
  campaigns: CampaignWithCounts[];
}

const statusConfig: Record<string, { label: string; className: string }> = {
  draft: {
    label: 'Draft',
    className: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  },
  sending: {
    label: 'Sending',
    className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-400',
  },
  sent: {
    label: 'Sent',
    className: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400',
  },
  cancelled: {
    label: 'Cancelled',
    className: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-400',
  },
};

function getUniqueRespondentCount(invitations: { respondent_id: string }[]): number {
  return new Set(invitations.map(i => i.respondent_id)).size;
}

function getFirstRespondentId(invitations: { respondent_id: string }[]): string | null {
  return invitations.length > 0 ? invitations[0].respondent_id : null;
}

export function CampaignList({ campaigns }: CampaignListProps) {
  if (campaigns.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-12 text-center">
        <Layers className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          No campaigns yet
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Create a campaign to send multiple surveys to your respondents at once.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
      <div className="min-w-full">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Campaign
              </th>
              <th scope="col" className="hidden sm:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Status
              </th>
              <th scope="col" className="hidden md:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Surveys
              </th>
              <th scope="col" className="hidden md:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Recipients
              </th>
              <th scope="col" className="hidden sm:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Date
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {campaigns.map((campaign) => {
              const surveyCount = campaign.survey_campaign_surveys?.[0]?.count ?? 0;
              const recipientCount = getUniqueRespondentCount(campaign.survey_invitations ?? []);
              const firstRespondentId = getFirstRespondentId(campaign.survey_invitations ?? []);
              const status = statusConfig[campaign.status] ?? statusConfig.draft;
              const campaignUrl = firstRespondentId
                ? `${window.location.origin}/campaigns/${campaign.id}/respond/${firstRespondentId}`
                : null;

              return (
                <tr key={campaign.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-6 py-4">
                    <div className="flex flex-col space-y-1">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {campaign.name}
                      </div>
                      {campaign.description && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1">
                          {campaign.description}
                        </p>
                      )}
                      <div className="sm:hidden flex items-center flex-wrap gap-2 mt-1">
                        <span className={cn(
                          "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
                          status.className
                        )}>
                          {status.label}
                        </span>
                        <span className="inline-flex items-center text-xs text-gray-500 dark:text-gray-400">
                          <FileText className="h-3 w-3 mr-1" />
                          {surveyCount}
                        </span>
                        <span className="inline-flex items-center text-xs text-gray-500 dark:text-gray-400">
                          <Users className="h-3 w-3 mr-1" />
                          {recipientCount}
                        </span>
                        <span className="inline-flex items-center text-xs text-gray-500 dark:text-gray-400">
                          <Calendar className="h-3 w-3 mr-1" />
                          {new Date(campaign.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="hidden sm:table-cell px-6 py-4 whitespace-nowrap">
                    <span className={cn(
                      "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
                      status.className
                    )}>
                      {status.label}
                    </span>
                  </td>
                  <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap">
                    <div className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400">
                      <FileText className="h-4 w-4 mr-1.5" />
                      {surveyCount}
                    </div>
                  </td>
                  <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap">
                    <div className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400">
                      <Users className="h-4 w-4 mr-1.5" />
                      {recipientCount}
                    </div>
                  </td>
                  <td className="hidden sm:table-cell px-6 py-4 whitespace-nowrap">
                    <div className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400">
                      <Calendar className="h-4 w-4 mr-1.5" />
                      {new Date(campaign.created_at).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <Link
                        to={`/campaigns/${campaign.id}/respondents`}
                        title="View recipients"
                      >
                        <Button variant="outline" size="sm" type="button">
                          <Users className="h-4 w-4" />
                        </Button>
                      </Link>
                      {campaignUrl && (
                        <a
                          href={campaignUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Open campaign survey"
                        >
                          <Button variant="outline" size="sm" type="button">
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
